import React from 'react'
import {
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Stack,
  Grid,
  GridItem,
  Box,
  Center,
  useColorModeValue,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import { BsCircle } from 'react-icons/bs'
import { ImCross } from 'react-icons/im'

import {
  TwitterShareButton,
  TwitterIcon,
  FacebookShareButton,
  FacebookIcon,
} from 'react-share'
import truncate from 'lodash/truncate'

import { Quiz, Stats } from 'types/generated/graphql'
import { LayoutEmbedQuiz } from 'components/Common/LayoutEmbedQuiz'

export interface QuizProps {
  quiz: Quiz
  quizOptions: string[]
  quizStats?: Stats
  selectedChoice: string | undefined
}

const correctText = css`
  -webkit-text-stroke: 0.5px white;
  -webkit-text-fill-color: red;
`
const incorrectText = css`
  -webkit-text-stroke: 0.5px white;
  -webkit-text-fill-color: #4169e1;
`

export const MultipleChoiceEmbedPlayAnswer: React.FC<QuizProps> = ({
  quiz,
  quizOptions,
  quizStats,
  selectedChoice,
}) => {
  const correct = quiz.content.correct
  const buttonNextColor = useColorModeValue('buttonNextLight', 'buttonNextDark')
  const { origin } = new URL(window.location.href)
  const shareUrl = `${origin}/quiz/${quiz.id}/play`
  const sendOnLink = quiz.snsInfo.destinationLink ?? ''
  const sendOnLinkText = quiz.snsInfo.destinationLinkText
    ? truncate(quiz.snsInfo.destinationLinkText, {
        length: 25,
      })
    : 'リンク'

  const deafultSnsShareText = quiz.snsInfo.shareText
    ? quiz.snsInfo.shareText
    : `「${quiz.content.question}」を解きました！`

  const description = () => {
    let stats = ''
    if (quiz.content.description) {
      return `解説：${quiz.content.description}`
    }
    if (quizStats) {
      stats = `(正答率${quizStats.stats[0].percent})`
    }
    return `Q：${quiz.content.question}${stats}`
  }

  return (
    <LayoutEmbedQuiz quiz={quiz} imageUrl={quiz.content.answerImageUrl!}>
      <VStack w="100%" spacing={3}>
        <Heading
          css={selectedChoice === correct ? correctText : incorrectText}
          as="h3"
          size="lg"
          textAlign="center"
        >
          {selectedChoice === correct ? '正解' : '不正解'}
        </Heading>

        <Text fontSize={{ base: 'md', sm: 'lg' }} mb="1rem" color="white">
          {description()}
        </Text>

        <Stack w="100%" mb="2rem" spacing={4} direction="row" align="center">
          <Grid
            w="100%"
            templateRows={{ base: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)' }}
            templateColumns={{
              base: 'repeat(1, 1fr)',
              md:
                selectedChoice === correct
                  ? 'repeat(2, 1fr)'
                  : 'repeat(4, 1fr)',
            }}
            gap={4}
          >
            {quizOptions.map((quiz, index) => {
              // 選択肢の順番を保持するためループ処理を行う
              // 正解の場合は、正解のみを表示する
              // 不正解の場合は、正解と選択した値を表示する
              if (quiz === correct) {
                // 正解の場合
                return (
                  <GridItem
                    w="100%"
                    rowSpan={{ base: 1, md: 1 }}
                    colSpan={{ base: 1, md: 2 }}
                    colorscheme="teal"
                    key={'quizAnswer-' + index}
                  >
                    <Box
                      boxShadow="base"
                      p="3"
                      rounded="md"
                      bg="whiteAlpha.800"
                      w="100%"
                    >
                      <Center color="red">
                        <BsCircle />：<Text fontWeight="bold">{quiz}</Text>
                      </Center>
                    </Box>
                  </GridItem>
                )
              } else if (quiz === selectedChoice) {
                // 不正解の場合
                return (
                  <GridItem
                    w="100%"
                    rowSpan={{ base: 1, md: 1 }}
                    colSpan={{ base: 1, md: 2 }}
                    colorscheme="teal"
                    key={'quizAnswer-' + index}
                  >
                    <Box
                      boxShadow="base"
                      p="3"
                      rounded="md"
                      bg="whiteAlpha.800"
                      w="100%"
                    >
                      <Center color="#4169e1">
                        <ImCross />：{quiz}
                      </Center>
                    </Box>
                  </GridItem>
                )
              }
              // 未選択かつ不正解の選択肢は表示しない
              return null
            })}
          </Grid>
        </Stack>

        <HStack>
          <TwitterShareButton
            url={shareUrl}
            title={deafultSnsShareText}
            hashtags={['qukker']}
          >
            <TwitterIcon size={32} borderRadius={10} />
          </TwitterShareButton>
          <FacebookShareButton
            url={shareUrl}
            quote={deafultSnsShareText}
            hashtag={'qukker'}
          >
            <FacebookIcon size={32} borderRadius={10} />
          </FacebookShareButton>
        </HStack>

        {sendOnLink && (
          <Button
            backgroundColor={buttonNextColor}
            mb={3}
            bottom="0"
            as="a"
            target="_blank"
            href={sendOnLink}
          >
            {sendOnLinkText}
          </Button>
        )}
      </VStack>
    </LayoutEmbedQuiz>
  )
}
